import { compact } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Card } from '../../components/Card/Card';
import { Container } from '../../components/Container/Container';
import { FilterBar } from '../../components/FilterBar/FilterBar';
import { AsyncSearch } from '../../components/Form/AsyncSearch/AsyncSearch';
import { Form } from '../../components/Form/Form';
import { TextField } from '../../components/Form/TextField/TextField';
import { Icon } from '../../components/Icon/Icon';
import { useModalPresenter } from '../../components/Modal/PresentedModal';
import { Pagination } from '../../components/Pagination/Pagination';
import { ShopName } from '../../components/ShopName/ShopName';
import { ColumnContent, SortableList } from '../../components/SortableList/SortableList';
import { t } from '../../locales/translate';
import { useData, usePaginatedResponse } from '../../model/data';
import { serializeQuery } from '../../model/filter';
import { useQueryAsState } from '../../model/history';
import { useMeta } from '../../model/meta';
import { editNews, getNews, News } from '../../model/news';
import { handleError } from '../../model/notification';
import { newsDetailsPath } from '../../routes';
import { formatDateTime } from '../../util/util';
import { CreateNewsItem } from './create_news_item';
import './News.css';

export function News(): JSX.Element {
  // HOOKS
  useMeta({ title: t.model.news._plural });
  const [params, setParams] = useQueryAsState();
  const modal = useModalPresenter();

  const paginator = usePaginatedResponse(
    (page, perPage) =>
      getNews({
        term: serializeQuery(params.query),
        shopId: params.shopId,
        page,
        perPage,
      }),
    serializeQuery(params.query) + JSON.stringify(params)
  );
  const news = useData((db) => compact(paginator.currentIds.map((e) => db.table('news').find(e))));

  function handleOnUpdateOrder(order: number): void {
    if (order < paginator.perPage * paginator.page) {
      setParams({
        ...params,
        page: 0,
      });
    }
  }

  return (
    <Container className="news">
      <Card
        className="news__list"
        title={t.model.news._plural}
        elevation={2}
        pagination={<Pagination {...paginator} />}
        fullbleed
        actions={<Icon onClick={() => modal.present(CreateNewsItem, { shopId: params.shopId })} name="plus" />}
      >
        <SortableList
          loading={paginator.loading}
          error={paginator.error}
          items={news}
          defaultSort={{
            sortBy: 'order',
            sortAscending: true,
          }}
          actions={
            <div className="news__filter-bar">
              <FilterBar filters={['query']}></FilterBar>
              <Form
                model={params}
                onInput={(value) => {
                  setParams({
                    ...params,
                    ...value,
                    page: 0,
                  });
                }}
              >
                <AsyncSearch label="Shop" name="shopId" searchIn={['shop']}></AsyncSearch>
              </Form>
            </div>
          }
          rowLink={(row) => newsDetailsPath(row)}
          columns={[
            {
              label: t.model.datetime.createdAt,
              dataKey: 'createdAt',
              sortable: true,
              cellRenderer: ({ createdAt }) => formatDateTime(createdAt),
            },
            {
              label: t.general.title,
              dataKey: 'titleEn',
              sortable: true,
              cellRenderer: (row) => row.title,
            },
            {
              label: t.model.shop._name,
              dataKey: 'shopId',
              sortable: true,
              cellRenderer: ({ shopId }) => shopId && <ShopName id={shopId} />,
            },
            // TODO, make render function and account for all items from the same shop
            ...(params.shopId
              ? ([
                  {
                    label: t.model.news.order,
                    dataKey: 'order',
                    sortable: true,
                    cellRenderer: (row: News) => <NewsOrder newsItem={row} onUpdateOrder={handleOnUpdateOrder} />,
                    disableLink: true,
                  },
                ] as ColumnContent<News>[])
              : []),
          ]}
        />
      </Card>
    </Container>
  );
}

export function NewsOrder({
  newsItem,
  onUpdateOrder,
}: {
  newsItem: News;
  onUpdateOrder: (order: number) => void;
}): JSX.Element {
  const [order, setOrder] = useState(newsItem.order);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setOrder(newsItem.order);
  }, [newsItem]);

  async function handleOnBlur(): Promise<void> {
    if (order === null || order === undefined) {
      return;
    }
    if (newsItem.order === order) {
      return;
    }
    setLoading(true);
    try {
      await editNews(newsItem.id, {
        order,
        image: undefined,
      });
      onUpdateOrder(order);
    } catch (e) {
      handleError(e);
    }
    setLoading(false);
  }

  return (
    <TextField
      className="news-list__order-input"
      disabled={loading}
      name="order"
      type="number"
      value={order}
      onChange={(m) => setOrder(Number(m))}
      onBlur={handleOnBlur}
      loading={loading}
    />
  );
}
